import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Presence } from "@/components/Presence";
import { api, errMsg } from "@/lib/api";

export default function LabPicker() {
  const nav = useNavigate();
  const [labs, setLabs] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    api.labs()
      .then((rows) => {
        if (rows.length === 1) return nav(`/l/${rows[0].slug}`, { replace: true });
        setLabs(rows);
      })
      .catch(() => setLabs([]));
  }, [nav]);

  const enterCode = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const lab = await api.resolveCode(code.trim());
      nav(`/l/${lab.slug}`, { state: { code: code.trim() } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const accent = hover || "#C87D55";

  return (
    <div
      className="grain lamp relative min-h-screen overflow-hidden"
      style={{ "--lab-accent": accent, background: "var(--bg-primary)", transition: "--lab-accent 600ms" }}
      data-testid="lab-picker"
    >
      <div className="pointer-events-none absolute right-[-14%] top-[-6%] z-0 w-[62%] opacity-50 lg:opacity-70">
        <Presence state="reflecting" accent={accent} height={520} />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-5xl px-6 pb-20 pt-16 lg:px-12 lg:pt-28">
        <div className="flex items-start gap-5">
          <span className="mt-1.5 h-16 w-[2px] shrink-0 rounded-full" style={{ background: `linear-gradient(180deg, transparent, ${accent}, transparent)` }} />
          <div>
            <p className="eyebrow rise">Griot · a keeper of what was said</p>
            <h1 className="rise rise-1 mt-4 max-w-[34rem] font-display text-[40px] font-light leading-[1.05] tracking-tight sm:text-[52px] lg:text-[64px]">
              Whose memory<br />shall we sit with?
            </h1>
            <p className="rise rise-2 mt-6 max-w-[30rem] text-[14.5px] leading-[1.9]" style={{ color: "var(--text-dim)" }}>
              Each Lab keeps its own record of what was said, made and felt. I speak only from the one you choose — nothing travels between them.
            </p>
          </div>
        </div>

        {labs === null ? (
          <p className="mt-16 flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--text-faint)" }}>
            <Loader2 size={13} className="animate-spin" /> finding the Labs
          </p>
        ) : labs.length === 0 ? (
          <p className="mt-16 max-w-md text-[14px] leading-relaxed" data-testid="lab-picker-empty" style={{ color: "var(--text-dim)" }}>
            No Lab is open to visitors just now. The archives are still here — come back a little later and I'll let you in.
          </p>
        ) : (
          <ul className="mt-16 max-w-3xl border-t" style={{ borderColor: "var(--border-linen)" }}>
            {labs.map((l, i) => (
              <li key={l.slug} className={`rise rise-${(i % 4) + 2} min-w-0 border-b`} style={{ borderColor: "var(--border-linen)" }}>
                <button
                  data-testid={`lab-card-${l.slug}`}
                  onClick={() => nav(`/l/${l.slug}`)}
                  onMouseEnter={() => setHover(l.accent)}
                  onMouseLeave={() => setHover(null)}
                  className="group flex w-full min-w-0 items-center gap-5 py-7 pr-2 text-left transition-[padding] duration-500 hover:pl-3"
                  style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
                >
                  <span className="font-mono text-[10px]" style={{ color: l.accent }}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="block min-w-0 flex-1">
                    <span className="block font-display text-[26px] font-light leading-tight transition-colors sm:text-[30px]" style={{ color: "var(--text-main)" }}>
                      {l.name}
                    </span>
                    <span className="mt-1.5 block max-w-xl text-[13px] leading-relaxed" style={{ color: "var(--text-faint)" }}>
                      {l.blurb || "A record of what this Lab made together."}
                    </span>
                    {l.requires_code && (
                      <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "#D4A359" }}>
                        <KeyRound size={10} /> code required
                      </span>
                    )}
                  </span>
                  <ArrowRight size={20} className="shrink-0 transition-transform duration-500 group-hover:translate-x-1.5" style={{ color: l.accent }} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="rise rise-5 mt-14 max-w-sm">
          <p className="font-mono text-[9.5px] uppercase tracking-[0.24em]" style={{ color: "var(--text-faint)" }}>carrying a code?</p>
          <div className="mt-3 flex items-center gap-2">
            <input
              data-testid="lab-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && enterCode()}
              placeholder="Lab code"
              className="min-w-0 flex-1 rounded-full border bg-transparent px-4 py-2.5 font-mono text-[12px] uppercase tracking-[0.18em] placeholder:normal-case placeholder:tracking-normal focus:outline-none"
              style={{ borderColor: "var(--border-linen)", color: "var(--text-main)" }}
            />
            <button
              data-testid="lab-code-submit"
              onClick={enterCode}
              disabled={busy}
              className="shrink-0 rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors disabled:opacity-50"
              style={{ borderColor: `${accent}80`, color: accent }}
            >
              {busy ? "checking" : "enter"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
